import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { MessageSquare, Send, Loader2, Sparkles, RotateCcw } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SUGGESTED_PROMPTS = [
  "How much does roof cleaning cost?",
  "I need my driveway pressure washed",
  "What's included in a full house wash?",
  "I have a commercial property that needs cleaning",
];

function generateSessionId() {
  return 'sess_' + Date.now() + '_' + Math.random().toString(36).substring(2, 9);
}

const QuoteAssistant = () => {
  const [sessionId, setSessionId] = useState(() => generateSessionId());
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  const sendMessage = async (text) => {
    if (!text.trim() || isStreaming) return;

    const userMsg = { role: 'user', content: text.trim() };
    setMessages((prev) => [...prev, userMsg]);
    setInput('');
    setIsStreaming(true);

    // Add placeholder for assistant
    setMessages((prev) => [...prev, { role: 'assistant', content: '' }]);

    try {
      const response = await fetch(`${API}/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId, message: text.trim() }),
      });

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const data = JSON.parse(line.slice(6));
            if (data.type === 'delta') {
              setMessages((prev) => {
                const updated = [...prev];
                const last = updated[updated.length - 1];
                if (last && last.role === 'assistant') {
                  updated[updated.length - 1] = {
                    ...last,
                    content: last.content + data.content,
                  };
                }
                return updated;
              });
            } else if (data.type === 'error') {
              setMessages((prev) => {
                const updated = [...prev];
                updated[updated.length - 1] = {
                  role: 'assistant',
                  content: data.content,
                };
                return updated;
              });
            }
          } catch (_) {
            // skip malformed lines
          }
        }
      }
    } catch (err) {
      console.error('Chat error:', err);
      setMessages((prev) => {
        const updated = [...prev];
        updated[updated.length - 1] = {
          role: 'assistant',
          content: 'Sorry, I had trouble connecting. Please try again.',
        };
        return updated;
      });
    } finally {
      setIsStreaming(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(input);
  };

  const handleNewChat = () => {
    setSessionId(generateSessionId());
    setMessages([]);
    setInput('');
  };

  const hasMessages = messages.length > 0;

  return (
    <section id="quote-assistant" className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
      <div className="max-w-4xl mx-auto">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-10">
          <div className="inline-flex items-center gap-2 bg-green-50 border border-green-200 rounded-full px-4 py-1.5 mb-4">
            <Sparkles className="h-4 w-4 text-green-700" />
            <span className="text-sm font-semibold text-green-800">AI-Powered</span>
          </div>
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4" data-testid="quote-assistant-title">
            Instant Quote Estimator
          </h2>
          <p className="text-lg text-gray-600">
            Describe your property and cleaning needs — our AI assistant will give you a rough price estimate in seconds.
          </p>
        </div>

        {/* Chat Card */}
        <Card className="border-2 border-green-100 shadow-xl overflow-hidden" data-testid="quote-assistant-card">
          {/* Chat Header */}
          <div className="bg-gradient-to-r from-green-700 to-emerald-700 px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-white/20 p-2 rounded-lg">
                <MessageSquare className="h-5 w-5 text-white" />
              </div>
              <div>
                <h3 className="text-white font-semibold text-lg">Quote Assistant</h3>
                <p className="text-green-100 text-sm">Ask about services & pricing</p>
              </div>
            </div>
            {hasMessages && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleNewChat}
                className="text-white hover:bg-white/20 gap-1.5"
                data-testid="new-chat-btn"
              >
                <RotateCcw className="h-4 w-4" />
                New Chat
              </Button>
            )}
          </div>

          <CardContent className="p-0">
            {/* Messages Area */}
            <div
              className="h-[400px] overflow-y-auto px-6 py-4 space-y-4 bg-gray-50/50"
              data-testid="chat-messages-area"
            >
              {!hasMessages && (
                <div className="flex flex-col items-center justify-center h-full text-center space-y-6">
                  <div className="bg-green-100 p-4 rounded-full">
                    <MessageSquare className="h-8 w-8 text-green-700" />
                  </div>
                  <div>
                    <p className="text-gray-700 font-semibold text-lg mb-1">
                      How can I help you today?
                    </p>
                    <p className="text-gray-500 text-sm max-w-md">
                      Tell me about your property and what cleaning services you need. I'll give you a rough estimate right away.
                    </p>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 w-full max-w-lg">
                    {SUGGESTED_PROMPTS.map((prompt, i) => (
                      <button
                        key={i}
                        onClick={() => sendMessage(prompt)}
                        className="text-left bg-white border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-700 hover:border-green-300 hover:bg-green-50 transition-all"
                        data-testid={`suggested-prompt-${i}`}
                      >
                        {prompt}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {messages.map((msg, index) => (
                <div
                  key={index}
                  className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                      msg.role === 'user'
                        ? 'bg-green-700 text-white rounded-br-md'
                        : 'bg-white border border-gray-200 text-gray-800 rounded-bl-md shadow-sm'
                    }`}
                    data-testid={`chat-message-${index}`}
                  >
                    <p className="text-sm leading-relaxed whitespace-pre-wrap">
                      {msg.content}
                      {msg.role === 'assistant' && msg.content === '' && isStreaming && (
                        <span className="inline-flex items-center gap-1 text-gray-400">
                          <Loader2 className="h-3 w-3 animate-spin" />
                          Thinking...
                        </span>
                      )}
                    </p>
                  </div>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>

            {/* Input Area */}
            <div className="border-t border-gray-200 bg-white px-4 py-3">
              <form onSubmit={handleSubmit} className="flex gap-2" data-testid="chat-input-form">
                <Input
                  ref={inputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Describe your property or ask about a service..."
                  disabled={isStreaming}
                  className="flex-1 border-gray-300 focus:border-green-500 focus:ring-green-500"
                  data-testid="chat-input"
                />
                <Button
                  type="submit"
                  disabled={isStreaming || !input.trim()}
                  className="bg-green-700 hover:bg-green-800 text-white px-4 shrink-0"
                  data-testid="chat-send-btn"
                >
                  {isStreaming ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4" />
                  )}
                </Button>
              </form>
              <p className="text-xs text-gray-400 mt-2 text-center">
                Estimates are approximate. Submit a Quote Request for an exact price.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </section>
  );
};

export default QuoteAssistant;
